<script setup lang="ts">
import { computed, ref } from "vue";
import { CollapsibleRoot, CollapsibleContent } from "reka-ui";

import { config } from "@/config";

import VoiceTranscriptUtterances from "@/components/CasePanels/RecordView/VoiceTranscript/VoiceTranscriptUtterances.vue";

import { getEvidenceText } from "@/services/UtilitiesService";

import { useBrandStore } from "@/stores/brand-store";
import { useMetadataStore } from "@/stores/metadata-store";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import type { Snapshot } from "@/models/Annotation";
import { caseFileService } from "@/services";

withDefaults(defineProps<{
  isSelected: boolean;
  snapshot: Snapshot;
  displayHeader?: boolean;
}>(), { displayHeader: true, })

const screenshotFallback =
  "data:image/jpeg;base64, iVBORw0KGgoAAAANSUhEUgAAAtgAAAA8BAMAAACut9CLAAAAG1BMVEX///8AAAA/Pz/f39+fn58fHx9/f3+/v79fX19u9CRbAAAACXBIWXMAAA7EAAAOxAGVKw4bAAACTklEQVR4nO2XzY/SYBCHpx/YHmlnCxzLGrLXtzExHruo9+IG0iN7wDOsiXqEVeK/7cxUqokJcTXSHn7PaZj5cXgfwvtBBAAAAAAAAAAAAAAAAAAAAAAAAAAAAADgP5ASBcPzET434p+RMzlgJOWfy/7d5h1kP4Wk+hfZBNlPYTSC7IvBUyeyH1IndXCsnfdq8UUK+Tw+3liHeFOTJorEkY38ivxcC/kmt1mRPc/KrtfTa3i3D4ZRdryT2q+PuffuTU3xalNR9vXgtENpfVhr4uVIZOsoZCpKLVS2ZdN6IT9KmD5edb2eXsMxB8M4p5XUhQvYy+ktDcqAaULxXjuUubCyhO4TOgqu6NZZIbItm5K/J/bWtOl4Of2GgzQYeluaS12URFLuKCIReEVRpR0tmoTKttE1PVqhsn9kw0r+JMvlwnW6mp7DdP9hWJAYli0i+0aeI/k0XzCNxbF2pAiahJ2AOrqnkRUqu80S3yZJsu14Pb2GafD8JJs+TvcmO568NoGVdiTyi2wbFZ/YCtuz2yzvYPo8TOHotI2EFLHJLuS0060h147JbrcRGw3eV1ao7DYrZUmfu15PrxGBi9MB6W2Dk+xneugN1tox2e0BaaN4traikW1Zf03s5zTrej29RgTu2qvfRK5+Knuwuq4pq5dOOybbEnLnbkaR3Li1sNtIk9Wrn4TGXa+n14hsv33UzORRo7Kj5f7oxtMb65hsSxwkZCPKmuKF3kZOWck94FHzl+D1fUEg+4JA9gWBbAAAAAAAAAAAAAAAAADgUnwHGn1kz6UuJGUAAAAASUVORK5CYII=";

defineEmits<(e: "click") => void>();
defineSlots<{ headerActions(props: { snapshot: Snapshot }): unknown }>();

const routeParams = useCaseRouteParams();

const screenshotSrc = ref<string>("");
const screenshotLoaded = ref<"IDLE" | "READY" | "LOADING" | "ERROR" | "DONE">("IDLE");

const lensConfig = computed(() => useBrandStore().viewerConfig.evidenceThemes?.highlightColors ?? {});

function isTranscriptAnnotation(arg: Snapshot): arg is Extract<Snapshot, { type: "TranscriptAnnotation" }> {
  return arg.type === "TranscriptAnnotation" || !!(arg as Record<string, unknown>).startTime;
}

function isCDMAnnotation(arg: Snapshot): arg is Extract<Snapshot, { type: "CDMAnnotation" }> {
  return arg.type === "CDMAnnotation";
}

function getHeader(fileReference: string): string {
  const { aiClassification = "Unknown", displayName } = useMetadataStore().filesByRef[fileReference] ?? {};

  if (aiClassification == null || displayName == null) return "";
  if (aiClassification === "Unknown") return displayName;

  return `${aiClassification}: ${displayName}`;
}

async function getScreenshotUrl(arg: Snapshot) {
  try {
    if (arg.type != null && arg.type !== "RectangleAnnotation" && arg.type !== "HighlightAnnotation") return;

    const { client, brand, casename } = routeParams.value;

    screenshotLoaded.value = "READY";
    screenshotSrc.value =
      arg.childReference === ""
        ? ""
        : await caseFileService.getSnapshot(client, brand, casename, arg.childReference, arg.revision);
  } catch {
    screenshotSrc.value = "";
    screenshotLoaded.value = "ERROR";
  }
}

function onScreenshotLoad() {
  screenshotLoaded.value = "DONE";
}

function onScreenshotError(e: Event) {
  console.error(e);

  screenshotSrc.value = "";
  screenshotLoaded.value = "ERROR";
}

function getText(line: string, text: string | string[]): string {
  if (Array.isArray(text)) {
    // An array of strings passed in
    text.forEach((t) => (line = line.replaceAll(t, `<b>${t}</b>`)));
    return line;
  }

  return line.replaceAll(text, `<b>${text}</b>`);
}
</script>
<template>
  <component
    :is="'context' in snapshot ? CollapsibleRoot : 'div'"
    class="tw-block tw-cursor-pointer tw-overflow-hidden tw-rounded-lg tw-border-2 hover:tw-border-gray-700"
    :class="{ 'tw-border-black/70': isSelected }"
    @click="$emit('click')"
  >
    <div v-if="displayHeader" class="tw-flex tw-items-center tw-justify-between tw-bg-gray-700 tw-p-1 tw-text-white">
      <h4 tabindex="0" class="leading-none tw-px-1 tw-text-base tw-text-white">
        {{ getHeader(snapshot.fileReference) }}
      </h4>
      <div v-if="$slots.headerActions != null" class="tw-text-section tw-relative tw-text-base">
        <slot name="headerActions" :snapshot="snapshot" />
      </div>
    </div>
    <div tabindex="0" aria-label="Annotation Content" class="tw-relative tw-flex tw-justify-center">
      <div v-if="isTranscriptAnnotation(snapshot)" tabindex="0" class="tw-w-full">
        <VoiceTranscriptUtterances :file-reference="snapshot.fileReference" :list="snapshot.utteranceList" />
      </div>
      <div v-else-if="isCDMAnnotation(snapshot)" tabindex="0" class="tw-w-full tw-px-4 tw-py-1.5">
        {{ snapshot.text }}
      </div>
      <div v-else class="tw-contents" @vue:mounted="getScreenshotUrl(snapshot)">
        <div v-if="screenshotLoaded === 'IDLE'" class="tw-grid tw-size-full tw-place-items-center tw-py-6">
          Initializing...
        </div>
        <template v-else>
          <img v-if="screenshotSrc === ''" :src="screenshotFallback" class="tw-min-h-20 tw-w-full tw-shadow-inner" />
          <img
            v-else
            crossorigin="use-credentials"
            class="tw-min-h-20 tw-w-full tw-shadow-inner"
            alt="Screenshot of document annotation section"
            :src="screenshotSrc"
            @load="onScreenshotLoad"
            @error="onScreenshotError"
          />
        </template>
      </div>
      <div
        v-if="['rejected', 'accepted', 'user', 'candidate'].includes(snapshot.status)"
        class="tw-absolute tw-top-0 tw-h-full tw-w-full tw-cursor-pointer tw-opacity-25"
        :class="{
          'tw-bg-red-400': snapshot.status === 'rejected',
          'bg-user': snapshot.status === 'accepted' || snapshot.status === 'user',
          'bg-candidate': snapshot.status === 'candidate',
        }"
      />
    </div>
    <div v-for="mark in snapshot.bookmarks" :key="mark.annotation" class="tw-grid tw-w-full tw-grid-cols-2">
      <div class="tw-flex tw-min-h-9 tw-items-center tw-gap-1 tw-bg-gray-700 tw-p-2 tw-text-white">
        <template v-for="color in [lensConfig[mark.theme]]" :key="color">
          <div v-if="color" :style="{ 'background-color': color }" class="tw-aspect-square tw-w-5 tw-rounded-sm" />
        </template>
        <span>{{ mark.theme }}</span>
      </div>
      <span class="tw-px-1 tw-py-px">{{ getEvidenceText(mark) }}</span>
    </div>
    <CollapsibleContent v-if="'context' in snapshot" class="tw-p-2 tw-font-semibold">
      <h4
        v-for="(line, index) in snapshot.context"
        :key="index"
        class="tw-mt-1"
        v-html="getText(line, snapshot.text[index])"
      />
    </CollapsibleContent>
  </component>
</template>
